import { PDP_ALTERNATE_FLOW_SELECTOR } from '../../utils/constants';
import { colors, spacing } from './variables';
const overflowWrapAnywhere = {
    overflowWrap: 'anywhere',
};
const hoverStyles = (theme) => ({
    backgroundColor: '#F6F6F6',
});
const focusStyles = {
    backgroundColor: colors.supplementary.white,
};
const borderStyles = (theme, status) => {
    if (status === 'success') {
        return `1px solid ${theme.statusColors.success.base}`;
    }
    return `1px solid ${theme.statusColors.error.base}`;
};
const getTextFieldStyles = (theme) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.supplementary.white,
    padding: spacing.xs,
    border: theme.isFlyingFlowers ? `1px solid ${colors.black[700]}` : `1px solid ${colors.grey.grey7}`,
    borderRadius: theme.isFlyingFlowers ? spacing.xxs : undefined,
    lineHeight: '1.625rem',
    '&:hover': hoverStyles(theme),
    '&:active': {
        backgroundColor: colors.supplementary.white,
    },
    '&:focus-within': focusStyles,
    [`${PDP_ALTERNATE_FLOW_SELECTOR} &`]: {
        padding: `${spacing.xs}`,
    },
    '&:focus-visible': {
        outline: '1px solid #000000',
        outlineOffset: '4px',
    },
    '& .MuiInputAdornment-root': {
        marginLeft: '10px',
    },
});
const getLabelStyles = (theme) => (Object.assign(Object.assign({}, theme.typography.small_bold), { display: 'block', color: theme.isFlyingFlowers ? colors.black[700] : undefined, [`${PDP_ALTERNATE_FLOW_SELECTOR} &`]: {
        marginBottom: '3px',
    } }));
const getErrorStyles = (theme, params) => ({
    border: borderStyles(theme, 'error'),
    backgroundColor: colors.supplementary.white,
    marginBottom: (params === null || params === void 0 ? void 0 : params.isFromPromoCodeForm) ? 0 : spacing.xs,
    padding: spacing.xs,
    '& input': {
        color: theme.isFlyingFlowers ? colors.supplementary.black : undefined,
    },
});
export const inputStyles = (theme, classes, params) => ({
    textFieldWrapper: {
        position: 'relative',
    },
    textField: getTextFieldStyles(theme),
    textFieldUnboxed: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: colors.supplementary.white,
        padding: `${spacing.small} ${spacing.xs}`,
    },
    label: getLabelStyles(theme),
    labelCompleted: Object.assign(Object.assign(Object.assign({ display: 'block' }, theme.typography.small_bold), theme.typographySpacing.none), { padding: '0 !important', color: theme.isFlyingFlowers ? colors.black[700] : undefined }),
    requiredIndicator: {
        color: colors.supplementary.black,
        marginLeft: '2px',
    },
    value: Object.assign(Object.assign({ width: '100%' }, theme.typography.small_regular), { backgroundColor: 'inherit', border: 0, '&:focus': {
            outline: 'none',
        } }),
    success: {
        border: borderStyles(theme, 'success'),
        padding: `${spacing.xs}`,
        '& .MuiInputBase-multiline': {
            alignItems: 'flex-start',
        },
        '& .MuiInputBase-multiline .MuiInputAdornment-root': {
            alignSelf: 'flex-start',
            marginTop: 0,
        },
    },
    error: getErrorStyles(theme, params),
    disabled: {
        backgroundColor: `${colors.grey.grey1}!important`,
        border: 'none !important', // To override the error/success border when disabled
        padding: theme.isFlyingFlowers ? `calc(${spacing.xs} - 1px)` : undefined,
        color: theme.isFlyingFlowers ? colors.black[600] : undefined,
        '&:hover': {
            backgroundColor: colors.grey.grey2,
            boxShadow: 'none !important',
        },
    },
    nonEditable: {
        backgroundColor: `${colors.grey.grey1} !important`,
        '&:hover': {
            backgroundColor: `${colors.grey.grey1} !important`,
        },
    },
    fullWidth: {
        width: '100%',
    },
    textContainer: {
        width: '100%',
        [`${PDP_ALTERNATE_FLOW_SELECTOR} &`]: {
            padding: '0',
        },
    },
    textFieldWithCharacterCount: {
        '& .MuiInputBase-inputMultiline': {
            paddingBottom: spacing.medium,
        },
    },
    inlineCharacterCount: Object.assign(Object.assign(Object.assign({ position: 'absolute', right: spacing.xs, bottom: spacing.xs }, theme.typography.xxs_regular), theme.typographySpacing.none), { color: colors.grey.grey6, pointerEvents: 'none' }),
    errorMessage: Object.assign({ color: colors.supplementary.black, backgroundColor: '#FCE7E7', padding: `0 ${spacing.xs}`, display: 'block' }, theme.typography.xxs_regular),
    icon: {
        color: colors.supplementary.black,
    },
    errorIcon: {
        color: theme.statusColors.error.base,
        fontSize: '1.5rem',
    },
    successIcon: {
        color: theme.statusColors.success.base,
        fontSize: '1.5rem',
    },
    diabledIcon: {
        color: colors.grey.grey5,
    },
    textContent: Object.assign(Object.assign(Object.assign({ paddingRight: spacing.xs, display: 'block' }, overflowWrapAnywhere), theme.typography.small_regular), { overflow: 'hidden', color: colors.supplementary.black, [`${PDP_ALTERNATE_FLOW_SELECTOR} &`]: {
            paddingRight: 0,
        } }),
    editIcon: {
        color: `${colors.supplementary.black} !important`,
    },
    touched: {
        backgroundColor: colors.supplementary.white,
    },
    completed: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        backgroundColor: colors.grey.grey1,
        padding: spacing.xs,
        border: '1px solid #2C8500',
        color: colors.supplementary.black,
        [`${PDP_ALTERNATE_FLOW_SELECTOR} &`]: {
            padding: spacing.xs,
            marginBottom: spacing.xs,
            color: colors.supplementary.black,
        },
        '&:hover': {
            border: '1px solid #2C8500',
        },
        '&:focus': {
            backgroundColor: colors.grey.grey1,
            border: '1px solid #2C8500',
        },
        '&$disabled': {
            cursor: 'auto',
            background: theme.isFlyingFlowers ? colors.grey.grey1 : undefined,
            '& *': {
                cursor: 'auto',
            },
            '&:hover': {
                border: '1px solid transparent',
            },
        },
        [`&.${classes === null || classes === void 0 ? void 0 : classes.error}`]: {
            border: `1px solid ${theme.statusColors.error.base}`,
            backgroundColor: colors.grey.grey1,
        },
    },
    buttonType: {
        cursor: 'pointer',
        '& *': {
            cursor: 'pointer',
        },
    },
    placeholder: {
        color: colors.grey.grey6,
    },
    newPlaceholder: {
        color: colors.black[600],
    },
    input: {
        marginBottom: spacing.xs,
        color: theme.isFlyingFlowers ? colors.black[600] : undefined,
    },
    inputSpacingLarge: { marginBottom: spacing.large },
    labelWrapper: {
        display: 'inline-block',
    },
});
